// WidgetAppearanceCard — Oct 2026 (Randy, Widget Roster Stage 3).
//
// "Becoming a collaborator will now actually open up a special section in that
// person's silo settings. And it's a different section, because of course
// their silo could also create a widget for their own website, which will be a
// separate thing."
//
// This is that section. It only appears when THIS silo has been accepted onto
// somebody else's widget, and it is deliberately separate from the silo's own
// Silo Anywhere panel. Settings are PER INVITATION, because the same
// practitioner can be on two businesses' widgets and want a different face and
// name on each.
import { useEffect, useRef, useState } from "react";
import { Storefront, UploadSimple, ArrowUUpLeft, SignOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fuel is fractions of a cent per question, so whole dollars would read "$0"
// all day and tell them nothing. Same formatter as the Silo Anywhere card.
const fuel = (cents) => {
  const c = Number(cents || 0);
  if (!c) return "$0.00";
  return c < 100 ? `$${(c / 100).toFixed(3)}` : `$${(c / 100).toFixed(2)}`;
};

const AppearanceRow = ({ entry, onChange, onLeave }) => {
  const [name, setName] = useState(entry.display_name || "");
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(Date.now());
  const fileRef = useRef(null);

  const shown = entry.effective_avatar_url;

  const patch = async (body) => {
    setBusy(true);
    try {
      const r = await api.patch(`/me/widget-invitations/${entry.id}/appearance`, body);
      onChange(r.data);
      setBust(Date.now());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save that.");
    } finally { setBusy(false); }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/me/widget-invitations/${entry.id}/avatar`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data);
      setBust(Date.now());
      toast.success("Photo set.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally { setBusy(false); if (e.target) e.target.value = ""; }
  };

  const clearPhoto = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/me/widget-invitations/${entry.id}/avatar`);
      onChange(r.data);
      setBust(Date.now());
    } catch (e) {
      toast.error("Could not remove that photo.");
    } finally { setBusy(false); }
  };

  return (
    <div className="border hairline rounded-lg p-4" data-testid={`widget-appearance-row-${entry.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-full overflow-hidden bg-[#EFEBE0] border hairline shrink-0 flex items-center justify-center">
          {shown ? (
            <img src={`${BACKEND_URL}${shown}?t=${bust}`} alt="" className="w-full h-full object-cover"
              data-testid={`widget-appearance-avatar-${entry.id}`} />
          ) : (
            <Storefront size={20} weight="duotone" className="text-[#A8A6A1]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[#1C1B1A]">
            On <strong>{entry.host_silo_name || "a business"}</strong>'s website
          </div>
          <p className="text-[11px] text-[#6B6A68] mt-0.5">
            This is the name and face their visitors pick from.
          </p>
          {entry.usage?.asks_total > 0 && (
            <p className="text-[11px] text-[#4A5D4E] mt-1 tabular-nums"
              data-testid={`widget-appearance-usage-${entry.id}`}>
              {entry.usage.asks_today} question{entry.usage.asks_today === 1 ? "" : "s"} today ·{" "}
              {entry.usage.asks_total} in all · {fuel(entry.usage.cost_cents_total)} of your fuel
            </p>
          )}

          <input
            value={name}
            maxLength={48}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => { if ((name || "") !== (entry.display_name || "")) patch({ display_name: name }); }}
            placeholder={entry.member_silo_name || "Your name"}
            data-testid={`widget-appearance-name-${entry.id}`}
            className="mt-2 w-full max-w-sm text-sm px-3 py-2 rounded-md border hairline bg-white text-[#1C1B1A]"
          />

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <button type="button" disabled={busy} onClick={() => fileRef.current?.click()}
              data-testid={`widget-appearance-upload-${entry.id}`}
              className="text-xs px-3 py-1.5 rounded-md border-2 border-[#1C1B1A] bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] hover:border-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-60">
              <UploadSimple size={12} weight="duotone" /> Upload a photo
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
            {!entry.use_silo_image && entry.avatar_url && (
              <button type="button" disabled={busy} onClick={clearPhoto}
                data-testid={`widget-appearance-use-silo-${entry.id}`}
                className="text-xs px-3 py-1.5 rounded-md border hairline text-[#6B6A68] hover:text-[#1C1B1A] flex items-center gap-1.5 disabled:opacity-60">
                <ArrowUUpLeft size={12} weight="duotone" /> Use my silo's image
              </button>
            )}
            <button type="button" disabled={busy} onClick={() => onLeave(entry)}
              data-testid={`widget-appearance-leave-${entry.id}`}
              className="text-xs px-3 py-1.5 rounded-md border hairline text-[#6B6A68] hover:border-[#C16E5A] hover:text-[#C16E5A] flex items-center gap-1.5 disabled:opacity-60 ml-auto">
              <SignOut size={12} weight="duotone" /> Leave this widget
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function WidgetAppearanceCard({ silo }) {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/me/widget-invitations");
        setEntries((r.data.invitations || []).filter(
          (i) => i.status === "accepted" && i.member_silo_id === silo?.id));
      } catch { setEntries([]); }
    })();
  }, [silo?.id]);

  const leave = async (entry) => {
    try {
      await api.post(`/me/widget-invitations/${entry.id}/leave`);
      setEntries((l) => l.filter((e) => e.id !== entry.id));
      toast.message(`Left ${entry.host_silo_name || "that widget"}.`);
    } catch { toast.error("Could not leave that widget."); }
  };

  // Nothing to configure until somebody has actually put this silo on their
  // site, so the section stays out of the way entirely.
  if (!entries || entries.length === 0) return null;

  return (
    <section className="bg-[#FDFCF8] border hairline rounded-lg p-6 mb-6 siloart-keep"
      data-testid="widget-appearance-card">
      <div className="flex items-center gap-2 mb-1">
        <Storefront size={16} weight="duotone" className="text-[#C19A4A]" />
        <h3 className="font-serif-display text-lg text-[#1C1B1A]">
          Appearing on other websites
        </h3>
      </div>
      <p className="text-xs text-[#6B6A68] leading-relaxed max-w-2xl mb-4">
        This silo has been added to someone else's site, so their visitors can pick you from a
        list and ask you directly. Choose the name and face they see — a photo of you usually
        works better than artwork in a professional setting. Set separately for each website,
        and nothing here touches your own widget or your Public QA page.
      </p>
      <div className="space-y-3">
        {entries.map((e) => (
          <AppearanceRow key={e.id} entry={e} onLeave={leave}
            onChange={(updated) => setEntries((l) => l.map((x) => (x.id === updated.id ? updated : x)))} />
        ))}
      </div>
    </section>
  );
}
